import * as env from 'config/environment';
import { formatDate } from 'utils/formatter';
import { getProxyAgent, stringifyWithoutQuoteOnKey } from 'utils/utils';
import { chartLegTypes } from 'components/features/utils/chart';

interface Iheader {
  Accept: string;
  'Content-Type': string;
  'X-API-KEY': string;
  [key: string]: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const execute = async (request: string): Promise<any> => {
  const method = 'POST';

  const headers: Iheader = {
    Accept: 'application/json',
    'Content-Type': 'application/graphql',
    'X-API-KEY': env.apiKey,
  };
  const proxyAgent = getProxyAgent();
  if (proxyAgent) {
    headers.AuthToken = proxyAgent?.sessionId ?? '-';
  } else if (env.TEST_REAL_DATA_SESSIONID !== '') {
    headers.AuthToken = env.TEST_REAL_DATA_SESSIONID;
  }

  const obj = {
    query: request,
  };
  const body = JSON.stringify(obj);

  const ret = await fetch(env.apiURL, {
    method,
    headers,
    body,
  });

  if (ret.status === 503) {
    window.location.href = '/maintenance';
  }

  const json = await ret.json();
  return json.data;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const authExecute = async (request: string): Promise<any> => {
  const method = 'POST';

  const headers: Iheader = {
    Accept: 'application/json',
    'Content-Type': 'application/graphql',
    'X-API-KEY': env.apiKey,
  };
  const proxyAgent = getProxyAgent();
  if (proxyAgent) {
    headers.AuthToken = proxyAgent?.sessionId ?? '-';
  } else if (env.TEST_REAL_DATA_SESSIONID !== '') {
    headers.AuthToken = env.TEST_REAL_DATA_SESSIONID;
  }

  const obj = {
    query: request,
  };
  const body = JSON.stringify(obj);

  const ret = await fetch(env.apiURL, {
    method,
    headers,
    body,
  });

  if (ret.status === 503) {
    window.location.href = '/maintenance';
  }
  if (ret.status === 401) {
    window.location.href = '/';
  }

  const json = await ret.json();
  return json.data;
};

export type User = {
  ID: string;
  Name: string;
  MailAddress: string;
  ProfileImageURL: string;
  Charged: boolean;
  LastLoginDateTime: string;
  NewUserAndInverstorIDNotIssued: boolean;
  RegisteredWebPush: boolean;
};

type AuthenticateResult = {
  Authenticated: boolean;
  Registered: boolean;
  User: User;
};

export const authenticate = async (): Promise<AuthenticateResult> => {
  const req = `mutation { Authenticate { Authenticated, Registered, User { ID, Name, MailAddress, ProfileImageURL, Charged, LastLoginDateTime, NewUserAndInverstorIDNotIssued, RegisteredWebPush } } }`;
  const ret = await authExecute(req);

  return ret.Authenticate as AuthenticateResult;
};

export type GetUserResut = {
  ID: string;
  Birthday: string | null;
  AnnualIncome: number | null;
  InvestmentStartDate: string | null;
  HideAsset: boolean;
  HideAssetP: boolean;
  HideAssetD: boolean;
  LinkedMoneytree: boolean;
  AuthenticatedMoneytree: boolean | null;
  LinkedLine: boolean;
} & User;
export const getUser = async (): Promise<GetUserResut> => {
  const req = `{ GetUser { ID, Name, MailAddress, ProfileImageURL, Charged, Birthday, AnnualIncome, InvestmentStartDate, HideAsset, HideAssetP, HideAssetD, LastLoginDateTime, LinkedMoneytree, AuthenticatedMoneytree, LinkedLine } }`;
  const ret = await execute(req);

  return ret.GetUser as GetUserResut;
};

type UpdateUserResult = {
  Success: boolean;
};
type UpdateUserParam = {
  Birthday?: string;
  HideAsset?: boolean;
  HideAssetP?: boolean;
  HideAssetD?: boolean;
  Agree?: boolean;
  AnnualIncome?: number | null;
  InvestmentStartDate?: string; // yyyy/MM/dd
};
export const updateUser = async (
  param: UpdateUserParam
): Promise<UpdateUserResult> => {
  const jsonString = stringifyWithoutQuoteOnKey(param);
  const req = `mutation { UpdateUser(input: ${jsonString} ) { Success } }`;
  const ret = await execute(req);

  return ret.UpdateUser as UpdateUserResult;
};

type RegisterAccountResult = {
  BrokerID: string;
  Account: string;
};
export const registerAccount = async (
  brokerID: string,
  account: string
): Promise<RegisterAccountResult> => {
  const req = `mutation { RegisterAccount(input: { BrokerID: "${brokerID}", Account: "${account}" } ) { BrokerID, Account } }`;
  const ret = await execute(req);

  return ret.RegisterAccount as RegisterAccountResult;
};

type DeleteAccountResult = {
  BrokerID: string;
  Account: string;
};
export const deleteAccount = async (
  brokerID: string,
  account: string
): Promise<DeleteAccountResult> => {
  const req = `mutation { DeleteAccount(input: { BrokerID: "${brokerID}", Account: "${account}" } ) { BrokerID, Account } }`;
  const ret = await execute(req);

  return ret.DeleteAccount as DeleteAccountResult;
};

type DisclaimerNotificationItem = {
  ID: string;
  Type: number; // 1: お知らせ 2: 決算情報 3: 適時開示 4: コーポレートアクション 5: 優待・配当権利付最終日 6: 大株主情報
  Title: string;
  Content: string;
  InsTime: string;
  UpdTime: string | null;
};

export type GetDisclaimerResutItem = {
  ID: string;
  NotificationID: string | null;
  Title: string | null;
  Content: string | null;
  Agreed: boolean;
  ExternalLink: boolean;
  UpdTime: string;
  Notification: DisclaimerNotificationItem | null;
};

export type GetDisclaimerResut = {
  Items: Array<GetDisclaimerResutItem>;
};

export const getDisclaimer = async (): Promise<GetDisclaimerResut> => {
  const req = `{ GetDisclaimer { Items { ID, NotificationID,Title, Content, ExternalLink, Agreed, UpdTime, Notification { ID, Type, Title, Content, InsTime, UpdTime } } } }`;
  const ret = await execute(req);

  return ret.GetDisclaimer as GetDisclaimerResut;
};

type UpdateUserDisclaimerResut = {
  Success: boolean;
};

export type UpdateUserDisclaimerParam = Array<DisclaimerItem>;

export type DisclaimerItem = {
  ID: string;
  NotificationID: string;
};

export const updateUserDisclaimer = async (
  param: UpdateUserDisclaimerParam
): Promise<UpdateUserDisclaimerResut> => {
  const jsonString = stringifyWithoutQuoteOnKey(param);
  const req = `mutation { UpdateUserDisclaimer(DisclaimerIDs: ${jsonString} ) { Success } }`;
  const ret = await execute(req);

  return ret.UpdateUser as UpdateUserDisclaimerResut;
};

export type GetAssetResult = {
  Item: GetAssetResultItem | null;
  Items: Array<GetAssetResultItem>;
};

export type GetAssetResultItem = {
  Day: number;
  TotalAssetAmount: number | null;
  DiffTotalAssetAmount: number | null;
  DiffTotalAssetRate: number | null;
  MarginProfitOrLoss: number | null;
  TotalProfitOrLoss: number | null;
  DiffTotalProfitOrLoss: number | null;
  DiffTotalProfitOrLossRate: number | null;
  StockAmount: number | null;
  MarginAmount: number | null;
  RoboAdvisorAmount: number | null;
  USStockAmount: number | null;
  InvestmentTrustAmount: number | null;
  CashAmount: number | null;
  OtherAmount: number | null;
  UnlistedStockAmount: number | null;
  EtfEtnAmount: number | null;
  ForeignEtfEtnAmount: number | null;
  ReitAmount: number | null;
  ForeignReitAmount: number | null;
  BondAmount: number | null;
  ForeignBondAmount: number | null;
  EbAmount: number | null;
  ForeignEbAmount: number | null;
  RealEstateAmount: number | null;
  Incorrect: boolean;
};

const ComplementAssetItems = (
  assetItems: GetAssetResultItem[]
): GetAssetResultItem[] => {
  let newItems: GetAssetResultItem[] = [];
  if (assetItems.length === 0) return newItems;

  // 規定数(補填する日数も含む取得日数）
  const dataCnt = chartLegTypes[3];

  /* 期間内の値は補填しない場合  */
  newItems = assetItems.map(x => x);

  /* 前日の値をスライドさせて期間内を補填する場合 */
  // const toItem = assetItems[0];
  // const toYM = Math.floor(toItem.Day / 100)
  // const toY = Math.floor(toItem.Day / 10000);
  // const toM = toYM - (toY * 100) - 1;
  // const toD = toItem.Day - (toYM * 100);
  // const toDate = new Date(toY, toM, toD).getDate();
  // for(var n=0;n<assetItems.length;n++) {
  //   const base = assetItems[n];
  //
  //   for(var c=newItems.length;c<dataCnt;c++) {
  //     const compDay = new Date(toY, toM, toD);
  //     compDay.setDate(toDate - c);
  //     const nCompDay = Number(formatDate(compDay, 'yyyyMMdd'));
  //
  //     if (base.Day === nCompDay) {
  //       newItems.push(assetItems[n]);
  //       break;
  //     } else if (assetItems[n].Day < nCompDay) {
  //       newItems.push({ ...base, Day: nCompDay });
  //     }
  //   }
  // }

  const fromItem = newItems[newItems.length - 1];
  const fromYM = Math.floor(fromItem.Day / 100);
  const fromY = Math.floor(fromItem.Day / 10000);
  const fromM = fromYM - fromY * 100 - 1;
  const fromD = fromItem.Day - fromYM * 100;
  const fromDate = new Date(fromY, fromM, fromD).getDate();

  for (let c = 1; c < dataCnt; c++) {
    const appendDay = new Date(fromY, fromM, fromD);
    appendDay.setDate(fromDate - c);
    const nCompDay = Number(formatDate(appendDay, 'yyyyMMdd'));
    newItems.push({
      Day: nCompDay,
      TotalAssetAmount: null,
      DiffTotalAssetAmount: null,
      DiffTotalAssetRate: null,
      MarginProfitOrLoss: null,
      TotalProfitOrLoss: null,
      DiffTotalProfitOrLoss: null,
      DiffTotalProfitOrLossRate: null,
      StockAmount: null,
      MarginAmount: null,
      RoboAdvisorAmount: null,
      USStockAmount: null,
      InvestmentTrustAmount: null,
      CashAmount: null,
      OtherAmount: null,
      UnlistedStockAmount: null,
      EtfEtnAmount: null,
      ForeignEtfEtnAmount: null,
      ReitAmount: null,
      ForeignReitAmount: null,
      BondAmount: null,
      ForeignBondAmount: null,
      EbAmount: null,
      ForeignEbAmount: null,
      RealEstateAmount: null,
      Incorrect: false,
    });
    if (newItems.length === dataCnt) break;
  }
  return newItems;
};
export const getAsset = async (
  isComplement = false
): Promise<GetAssetResult> => {
  const req = `{ GetAsset { Item { TotalAssetAmount, DiffTotalAssetAmount, DiffTotalAssetRate, TotalProfitOrLoss, DiffTotalProfitOrLoss, DiffTotalProfitOrLossRate, StockAmount, MarginAmount, CashAmount, OtherAmount, RoboAdvisorAmount, USStockAmount, InvestmentTrustAmount, UnlistedStockAmount, EtfEtnAmount, ForeignEtfEtnAmount, ReitAmount, ForeignReitAmount, BondAmount, ForeignBondAmount, EbAmount, ForeignEbAmount, RealEstateAmount }, Items { Day, TotalAssetAmount, DiffTotalAssetAmount, DiffTotalAssetRate, TotalProfitOrLoss, DiffTotalProfitOrLoss, DiffTotalProfitOrLossRate, StockAmount, MarginAmount, CashAmount, OtherAmount, RoboAdvisorAmount, USStockAmount, InvestmentTrustAmount, UnlistedStockAmount, EtfEtnAmount, ForeignEtfEtnAmount, ReitAmount, ForeignReitAmount, BondAmount, ForeignBondAmount, EbAmount, ForeignEbAmount, RealEstateAmount, Incorrect } } }`;
  const ret = await execute(req);
  const asset = ret.GetAsset as GetAssetResult;
  if (isComplement) {
    // 規定数になるまで過去足を付与
    asset.Items = ComplementAssetItems(asset.Items);
  }
  return asset;
};
export type GetUserBrokerResult = {
  Items: Array<GetUserBrokerResultItem>;
};
// TODO: 取り込みが失敗したかどうかのフラグが必要
export type GetUserBrokerResultItem = {
  BrokerID: string;
  BrokerName: string;
  BrokerType: number;
  AggregateSource: number;
  Account: string;
  // TODO: Status = null は口座連携したが、一度もデータ取得していない状態
  Status: string | null;
  TotalAssetAmount: number;
  TotalProfitOrLoss: number;
  CashAmount: number;
  OtherAmount: number;
  Available: boolean;
  UpdTime: string;
  DiffTotalAssetAmount: number | null;
  DiffTotalAssetRate: number | null;
  DiffStockAmount: number | null;
  DiffStockRate: number | null;
  DiffMarginAmount: number | null;
  DiffMarginRate: number | null;
  DiffMarginProfitOrLoss: number | null;
  DiffMarginProfitOrLossRate: number | null;
  DiffRoboAdvisorAmount: number | null;
  DiffRoboAdvisorRate: number | null;
  DiffUSStockAmount: number | null;
  DiffUSStockRate: number | null;
  DiffInvestmentTrustAmount: number | null;
  DiffInvestmentTrustRate: number | null;
  DiffCashAmount: number | null;
  DiffCashRate: number | null;
  DiffOtherAmount: number | null;
  DiffOtherRate: number | null;
  DiffTotalProfitOrLoss: number | null;
  DiffUnlistedStockAmount: number | null;
  DiffUnlistedStockRate: number | null;
  DiffEtfEtnAmount: number | null;
  DiffEtfEtnRate: number | null;
  DiffForeignEtfEtnAmount: number | null;
  DiffForeignEtfEtnRate: number | null;
  DiffReitAmount: number | null;
  DiffReitRate: number | null;
  DiffForeignReitAmount: number | null;
  DiffForeignReitRate: number | null;
  DiffBondAmount: number | null;
  DiffBondRate: number | null;
  DiffForeignBondAmount: number | null;
  DiffForeignBondRate: number | null;
  DiffEbAmount: number | null;
  DiffEbRate: number | null;
  DiffForeignEbAmount: number | null;
  DiffForeignEbRate: number | null;
  DiffRealEstateAmount: number | null;
  DiffRealEstateRate: number | null;
  IsRegisteredBefore: boolean;
};

const getUserBroker_ = async (
  groupID?: string,
  from?: string, // yyyy/MM/dd
  to?: string // yyyy/MM/dd
): Promise<GetUserBrokerResult> => {
  const queryGroupID = groupID ? `"${groupID}"` : null;
  const queryFrom = from ? `"${from}"` : null;
  const queryTo = to ? `"${to}"` : null;

  const req = `{ GetUserBroker(groupID: ${queryGroupID}, from: ${queryFrom}, to: ${queryTo}) { Items { BrokerID, BrokerName, BrokerType, AggregateSource, Account, Status, TotalAssetAmount, TotalProfitOrLoss, CashAmount, OtherAmount, Available, UpdTime, DiffTotalAssetAmount, DiffTotalAssetRate, DiffStockAmount, DiffStockRate, DiffMarginAmount, DiffMarginRate, DiffMarginProfitOrLoss, DiffMarginProfitOrLossRate, DiffRoboAdvisorAmount, DiffRoboAdvisorRate, DiffUSStockAmount, DiffUSStockRate, DiffInvestmentTrustAmount, DiffInvestmentTrustRate, DiffUnlistedStockAmount, DiffUnlistedStockRate, DiffEtfEtnAmount, DiffEtfEtnRate, DiffForeignEtfEtnAmount, DiffForeignEtfEtnRate, DiffReitAmount, DiffReitRate, DiffForeignReitAmount, DiffForeignReitRate, DiffBondAmount, DiffBondRate, DiffForeignBondAmount, DiffForeignBondRate, DiffEbAmount, DiffEbRate, DiffForeignEbAmount, DiffForeignEbRate, DiffRealEstateAmount, DiffRealEstateRate, DiffCashAmount, DiffCashRate, DiffOtherAmount, DiffOtherRate, DiffTotalProfitOrLoss, IsRegisteredBefore } } }`;
  const ret = await execute(req);

  return ret.GetUserBroker as GetUserBrokerResult;
};
export const getUserBroker: typeof getUserBroker_ = async (
  groupID?: string,
  from?: string,
  to?: string
) => {
  const ret = await getUserBroker_(
    groupID ?? undefined,
    from ?? undefined,
    to ?? undefined
  );

  const result = ret.Items.flatMap(item => {
    return [item];
  });
  return { Items: result };
};

type GetOrdersResult = {
  Items: Array<GetOrdersResultItem>;
  TotalProfitOrLoss: number | null;
  OldestDate: string;
  LatestDate: string;
  TotalPageNum: number;
  PageNum: number;
};
export type GetOrdersResultItem = {
  BrokerID: string;
  BrokerName: string;
  TradeTime: string | null;
  Symbol: string;
  SymbolName: string;
  AccountType: number;
  ProductType: number;
  CashMargin: number;
  TradeType: number;
  Side: number;
  Qty: number | null;
  Price: number | null;
  OpenPrice: number | null;
  OpenPriceUSD: number | null;
  ProfitOrLoss: number | null;
  ProfitOrLossUSD: number | null;
  CurrencyUnit: string | null;
};
export const getOrders = async (
  reportType?: number,
  from?: string,
  to?: string,
  checkProductTypeList?: Array<number>,
  checkTradeClassificationList?: Array<number>,
  checkBrokerList?: Array<string>,
  pageNum?: number
): Promise<GetOrdersResult> => {
  const queryReportType = reportType ? `${reportType}` : null;
  const queryFrom = from ? `"${from}"` : null;
  const queryTo = to ? `"${to}"` : null;

  const queryCheckProductTypeList =
    checkProductTypeList !== undefined && checkProductTypeList.length !== 0
      ? `[${checkProductTypeList.map(item => `${item}`)}]`
      : null;

  const queryCheckTradeClassificationList =
    checkTradeClassificationList !== undefined &&
    checkTradeClassificationList.length !== 0
      ? `[${checkTradeClassificationList.map(item => `${item}`)}]`
      : null;

  const queryCheckBrokerList =
    checkBrokerList !== undefined && checkBrokerList.length !== 0
      ? `[${checkBrokerList.map(item => `"${item}"`)}]`
      : null;
  const queryPageNum = pageNum ? `${pageNum}` : null;

  const req = `{ GetOrders(reportType: ${queryReportType}, from: ${queryFrom}, to: ${queryTo}, checkProductTypeList: ${queryCheckProductTypeList}, checkTradeClassificationList: ${queryCheckTradeClassificationList}, checkBrokerList: ${queryCheckBrokerList}, pageNum: ${queryPageNum}) { Items { BrokerID, BrokerName, TradeTime, Symbol, SymbolName, AccountType, ProductType, CashMargin, TradeType, Side, Qty, Price, OpenPrice, OpenPriceUSD, ProfitOrLoss, ProfitOrLossUSD, CurrencyUnit } TotalProfitOrLoss, OldestDate, LatestDate, TotalPageNum, PageNum } }`;

  const ret = await execute(req);

  return ret.GetOrders as GetOrdersResult;
};
export const getSymbolOrders = async (
  symbol: string
): Promise<GetOrdersResult> => {
  const req = `{ GetSymbolOrders(symbol: "${symbol}") { Items { BrokerID, BrokerName, TradeTime, Symbol, SymbolName, AccountType, CashMargin, TradeType, Side, Qty, Price, ProductType, ProfitOrLoss } } }`;
  const ret = await execute(req);

  return ret.GetSymbolOrders as GetOrdersResult;
};

export type GetPositionsResult = {
  Items: Array<GetPositionsResultItem>;
};
export type GetPositionsResultItem = {
  BrokerID: string;
  Account: string;
  Symbol: string;
  SymbolName: string;
  AccountType: number;
  ProductType: number;
  CashMargin: number;
  Side: number;
  Qty: number | null;
  Price: number | null;
  CurrentAmount: number | null;
  CurrentAmountUSD: number | null;
  ProfitOrLoss: number | null;
  ProfitOrLossUSD: number | null;
  ValidationAmount: number | null;
  DiffProfitOrLoss: number | null;
  DiffProfitOrLossUSD: number | null;
  DiffProfitOrLossRate: number | null;
  CurrencyUnit: string | null;
  Available: boolean;
  UpdatedFlg: boolean;
  Status: string | null;
};

export const getPositions = async (
  groupID?: string,
  reportType?: number
): Promise<GetPositionsResult> => {
  const queryGroupID = groupID ? `"${groupID}"` : null;
  const queryReportType = reportType ? `${reportType}` : null;
  const req = `{ GetPositions(groupID: ${queryGroupID}, reportType: ${queryReportType} ) { Items { BrokerID, Account, Symbol, SymbolName, AccountType, ProductType, CashMargin, Side, Qty, Price, CurrentAmount, CurrentAmountUSD, ProfitOrLoss, ProfitOrLossUSD, ValidationAmount, DiffProfitOrLoss, DiffProfitOrLossUSD, DiffProfitOrLossRate, CurrencyUnit, Available, UpdatedFlg, Status } } }`;

  const ret = await execute(req);

  return ret.GetPositions as GetPositionsResult;
};

export const getSymbolPositions = async (
  symbol: string
): Promise<GetPositionsResult> => {
  const req = `{ GetSymbolPositions(symbol: "${symbol}") { Items { BrokerID, Symbol, SymbolName, AccountType, CashMargin, Side, Qty, Price, CurrentAmount, ProfitOrLoss, ProductType } } }`;
  const ret = await execute(req);

  return ret.GetSymbolPositions as GetPositionsResult;
};

export type GetSymbolResult = {
  Info: GetSymbolResultInfo;
  Price: GetSymbolResultPrice;
  Prices: Array<GetSymbolResultPrice>;
};

export type GetSymbolResultInfo = {
  Symbol: string;
  Name: string;
  Exchange: number;
  ProductType: number;
};
export type GetSymbolResultPrice = {
  Day: string;
  Close: number | null;
};

export const getSymbol = async (
  symbol: string
): Promise<GetSymbolResult | null> => {
  const req = `{ GetSymbol(symbol: "${symbol}") { Info { Symbol, Name, Exchange, ProductType }, Price { Day, Close }, Prices { Day, Close } } }`;
  const ret = await execute(req);

  return (ret ? ret.GetSymbol : null) as GetSymbolResult;
};

export type GetNewsResult = {
  ID: string;
  Title: string;
  Body: string;
  Image: string | null;
  PDFList: string[] | null;
  Date: string;
  Provider: string;
  Creator: string | null;
};
export type GetSymbolNewsResult = {
  Items: Array<GetNewsResult>;
};

export const getNews = async (id: string): Promise<GetNewsResult | null> => {
  const req = `{ GetNews(id: "${id}") { ID, Title, Body, Image, PDFList, Date, Provider, Creator } }`;
  const ret = await execute(req);

  return (ret ? ret.GetNews : null) as GetNewsResult;
};

export const getSymbolNews = async (
  symbol: string
): Promise<GetSymbolNewsResult | null> => {
  const req = `{ GetSymbolNews(symbol: "${symbol}") { Items { ID, Title, Body, Image, PDFList, Date, Provider, Creator } } }`;
  const ret = await execute(req);

  return (ret ? ret.GetSymbolNews : null) as GetSymbolNewsResult;
};

export type AssetFluctuation = {
  From: number;
  To: number;
  Amount: number;
  Rate: number;
};
export type GetAssetFluctuationResult = {
  Week: AssetFluctuation;
  Month: AssetFluctuation;
  Year: AssetFluctuation;
};
export const getAssetFluctuation = async (): Promise<GetAssetFluctuationResult> => {
  const req = `{ GetAssetFluctuation { Week { From, To, Amount, Rate }, Month { From, To, Amount, Rate }, Year { From, To, Amount, Rate } } }`;
  const ret = await execute(req);
  return ret.GetAssetFluctuation as GetAssetFluctuationResult;
};

export type GetPointItem = {
  Type: number;
  Title: string;
  Content: string;
  Point: number;
  From: string;
  To: string;
  Target: number;
  Current: number;
  GotTime: string;
};
export type GetMissionsResult = {
  Items: Array<GetPointItem>;
};
export type GetPointsResult = {
  TotalPoint: number;
  Expired: string;
  Items: Array<GetPointItem>;
};
export const getPoints = async (): Promise<GetPointsResult> => {
  const req = `{ GetPoints { TotalPoint, Expired, Items { Type, Title, Content, Point, From, To, Target, Current, GotTime } } }`;
  const ret = await execute(req);
  return ret.GetPoints as GetPointsResult;
};
export const getMissions = async (): Promise<GetMissionsResult> => {
  const req = `{ GetMissions { Items { Type, Title, Content, Point, From, To, Target, Current, GotTime } } }`;
  const ret = await execute(req);
  return ret.GetMissions as GetMissionsResult;
};

export type GetNotificationItem = {
  ID: string;
  Type: number; // 1: お知らせ 2: 決算情報 3: 適時開示 4: コーポレートアクション 5: 優待・配当権利付最終日 6: 大株主情報
  Title: string;
  Content: string;
  BeforeLogin: number;
  ReadStatus: boolean;
  InsTime: string;
  UpdTime: string;
};
export type GetNotificationResult = {
  Items: Array<GetNotificationItem>;
};
export const getNotifications = async (
  symbol?: string,
  func?: string
): Promise<GetNotificationResult> => {
  const querySymbol = symbol ? `"${symbol}"` : null;
  const queryFunc = func ? `"${func}"` : null;
  const req = `{ GetNotifications(symbol: ${querySymbol}, func: ${queryFunc}) { Items { ID, Type, Title, Content, BeforeLogin, ReadStatus, InsTime, UpdTime } } }`;
  const ret = await execute(req);
  return ret.GetNotifications as GetNotificationResult;
};
export type ReadNotificationResult = {
  Success: boolean;
};
export const setReadNotification = async (
  IDList: Array<string>
): Promise<ReadNotificationResult> => {
  const req = `mutation { ReadNotification(input: { IDList: [${IDList}] } ) { Success } }`;
  const ret = await execute(req);
  return ret.ReadNotification as ReadNotificationResult;
};

export type PeriodYield = {
  Day: PeriodYieldInfo;
  Week: PeriodYieldInfo;
  Month: PeriodYieldInfo;
  Year: PeriodYieldInfo;
  Items: Array<Performance>;
};
export type PeriodYieldInfo = {
  From: number;
  To: number;
  Yield: number;
  YieldWithoutEvaluation: number;
  TotalAssetAmount: number;
  DiffTotalAssetAmount: number;
};
export type Performance = {
  Date: string;
  Yield1M: number;
  Yield3M: number;
  Yield6M: number;
  Yield1Y: number;
  YieldAll: number;
};
export type InvestmentTrustYield = {
  TypeA: Array<Performance>;
  TypeB: Array<Performance>;
  TypeC: Array<Performance>;
};
export type PersonYield = {
  High: Array<Performance>;
  Low: Array<Performance>;
  Average: Array<Performance>;
};
export type InvestmentTrustInfo = {
  TypeA: InvestmentTrustSymbol;
  TypeB: InvestmentTrustSymbol;
  TypeC: InvestmentTrustSymbol;
};
export type InvestmentTrustSymbol = {
  Symbol: string;
  SymbolName: string;
  Return: number;
  StandardPrice: number;
  StandardPriceDate: string;
  NetAsset: number;
};
export type AccountInfo = {
  BrokerName: string;
  Account: string;
  TotalAssetAmount: number;
  DiffTotalAssetAmount: number;
  DiffTotalAssetRatio: number;
};
export type PositionInfo = {
  Symbol: string;
  SymbolName: string;
  ProductType: number;
  CashMargin: number;
  Side: number;
  ValidationAmount: number | null;
  ValidationAmountUSD: number | null;
  DiffValidationAmount: number | null;
  DiffValidationAmountUSD: number | null;
  DiffValidationRatio: number | null | null;
  CurrencyUnit: string | null;
};

export type ReportBase = {
  From: number | null;
  To: number | null;
  TotalAssetAmount: number | null;
  PreviousTotalAssetAmount: number | null;
  DiffTotalAssetAmount: number | null;
  DiffTotalAssetRate: number | null;
  DiffStockRate: number | null;
  DiffMarginRate: number | null;
  DiffMarginProfitOrLossRate: number | null;
  DiffRoboAdvisorRate: number | null;
  DiffUSStockRate: number | null;
  DiffInvestmentTrustRate: number | null;
  DiffCashRate: number | null;
  DiffOtherRate: number | null;
  DiffUnlistedStockRate: number | null;
  DiffEtfEtnRate: number | null;
  DiffForeignEtfEtnRate: number | null;
  DiffReitRate: number | null;
  DiffForeignReitRate: number | null;
  DiffBondRate: number | null;
  DiffForeignBondRate: number | null;
  DiffEbRate: number | null;
  DiffForeignEbRate: number | null;
  DiffRealEstateRate: number | null;
  AssetFluctuationThresholdPer?: number | null;
};
export type ReportTransition = {
  Day: number;
  TotalAsset: number | null;
  Stock: number | null;
  Margin: number | null;
  MarginProfitOrLoss: number | null;
  ForeignStock: number | null;
  InvestmentTrust: number | null;
  RoboAdvisor: number | null;
  Cash: number | null;
  Other: number | null;
  UnlistedStockAmount: number | null;
  EtfEtnAmount: number | null;
  ForeignEtfEtnAmount: number | null;
  ReitAmount: number | null;
  ForeignReitAmount: number | null;
  BondAmount: number | null;
  ForeignBondAmount: number | null;
  EbAmount: number | null;
  ForeignEbAmount: number | null;
  RealEstateAmount: number | null;
  Incorrect: boolean;
};
export type ReportBroker = {
  ID: string;
  Name: string;
  Account: string;
  AggregateSource: number;
  TotalAssetAmount: number;
  DiffTotalAssetAmount: number;
  DiffTotalAssetRate: number;
  DiffStockAmount: number;
  DiffStockRate: number;
  DiffMarginAmount: number;
  DiffMarginRate: number;
  DiffMarginProfitOrLoss: number;
  DiffMarginProfitOrLossRate: number;
  DiffRoboAdvisorAmount: number;
  DiffRoboAdvisorRate: number;
  DiffUSStockAmount: number;
  DiffUSStockRate: number;
  DiffInvestmentTrustAmount: number;
  DiffInvestmentTrustRate: number;
  DiffCashAmount: number;
  DiffCashRate: number;
  DiffOtherAmount: number;
  DiffOtherRate: number;
  IsRegisteredBefore: boolean;
};

export type ReportGoal = {
  ID: string;
  RefType: number;
  Title: string;
  TotalTargetAmount: number;
  TargetAmount: number | null;
  AchievementAmount: number | null;
  Completed: boolean;
};
export type ReportIndexPrice = {
  Amount: number;
  Rate: number;
  DateProperty: string;
  Incorrect?: boolean;
};

export type ReportIndex = {
  Day: number;
  Mine: ReportIndexPrice | null;
  NK225: ReportIndexPrice | null;
  TOPIX: ReportIndexPrice | null;
  DJI: ReportIndexPrice | null;
  SSI: ReportIndexPrice | null;
  USDJPY: ReportIndexPrice | null;
  BTCJPY: ReportIndexPrice | null;
};

export type ReportOrder = {
  TradeTime: string | null;
  Symbol: string;
  SymbolName: string;
  AccountType: number;
  ProductType: number;
  CashMargin: number;
  TradeType: number;
  Side: number;
  Qty: number;
  ProfitOrLoss: number;
  ProfitOrLossUSD: number;
};

export type ReportArticle = {
  Type: number;
  Title: string;
  ContentUrl: string;
  ImageUrl: string;
};

export type GetMonthlyReportResult = {
  CreatedAt: string | null;
  PreviousQuery: string | null;
  NextQuery: string | null;
  Base: ReportBase;
  Transitions: Array<ReportTransition>;
  Brokers: Array<ReportBroker>;
  Goals: Array<ReportGoal>;
  Indexes: Array<ReportIndex>;
  Orders: Array<ReportOrder>;
  Articles: Array<ReportArticle>;
} | null;

export type ReportPositionRanking = {
  IncreasedPositionList: Array<PositionInfo>;
  DecreasedPositionList: Array<PositionInfo>;
};

export type GetReportItemResult = {
  CreatedAt: string | null;
  PreviousQuery?: string | null;
  NextQuery?: string | null;
  Base: ReportBase;
  Articles?: ReportArticle[] | null;
  PositionRanking?: ReportPositionRanking | null;
};

export type GetReportResult = {
  Day: GetReportItemResult | null;
  Week: GetReportItemResult | null;
  Month: GetReportItemResult | null;
  Year: GetReportItemResult | null;
};
export const getReport = async (
  reportType?: number,
  target?: string
): Promise<GetReportResult> => {
  const queryReportType = reportType ? `${reportType}` : null;
  const queryTarget = target ? `"${target}"` : null;
  const req = `{ GetReport(reportType: ${queryReportType}, target: ${queryTarget}) { Day { CreatedAt, PreviousQuery, NextQuery, Base { From, To, TotalAssetAmount, PreviousTotalAssetAmount, DiffTotalAssetAmount, DiffTotalAssetRate, DiffStockRate, DiffMarginRate, DiffMarginProfitOrLossRate, DiffRoboAdvisorRate, DiffUSStockRate, DiffInvestmentTrustRate, DiffCashRate, DiffOtherRate, DiffUnlistedStockRate, DiffEtfEtnRate, DiffForeignEtfEtnRate, DiffReitRate, DiffForeignReitRate, DiffBondRate, DiffForeignBondRate, DiffEbRate, DiffForeignEbRate, DiffRealEstateRate, AssetFluctuationThresholdPer }, Articles { ContentUrl, ImageUrl, Title, Type }, PositionRanking { DecreasedPositionList { CashMargin, DiffValidationAmount, DiffValidationAmountUSD, DiffValidationRatio, ProductType, Side, Symbol, SymbolName, ValidationAmount, ValidationAmountUSD, CurrencyUnit }, IncreasedPositionList { CashMargin, DiffValidationAmount, DiffValidationAmountUSD, DiffValidationRatio, ProductType, Side, Symbol, SymbolName, ValidationAmount, ValidationAmountUSD, CurrencyUnit } } }, Week { CreatedAt, PreviousQuery, NextQuery, Base { From, To, TotalAssetAmount, PreviousTotalAssetAmount, DiffTotalAssetAmount, DiffTotalAssetRate, DiffStockRate, DiffMarginRate, DiffMarginProfitOrLossRate, DiffRoboAdvisorRate, DiffUSStockRate, DiffInvestmentTrustRate, DiffUnlistedStockRate, DiffEtfEtnRate, DiffForeignEtfEtnRate, DiffReitRate, DiffForeignReitRate, DiffBondRate, DiffForeignBondRate, DiffEbRate, DiffForeignEbRate, DiffRealEstateRate, DiffCashRate, DiffOtherRate, AssetFluctuationThresholdPer }, Articles { ContentUrl, ImageUrl, Title, Type }, PositionRanking { DecreasedPositionList { CashMargin, DiffValidationAmount, DiffValidationAmountUSD, DiffValidationRatio, ProductType, Side, Symbol, SymbolName, ValidationAmount, ValidationAmountUSD, CurrencyUnit }, IncreasedPositionList { CashMargin, DiffValidationAmount, DiffValidationAmountUSD, DiffValidationRatio, ProductType, Side, Symbol, SymbolName, ValidationAmount, ValidationAmountUSD, CurrencyUnit } } }, Month { CreatedAt, PreviousQuery, NextQuery, Base { From, To, TotalAssetAmount, PreviousTotalAssetAmount, DiffTotalAssetAmount, DiffTotalAssetRate, DiffStockRate, DiffMarginRate, DiffMarginProfitOrLossRate, DiffRoboAdvisorRate, DiffUSStockRate, DiffInvestmentTrustRate, DiffUnlistedStockRate, DiffEtfEtnRate, DiffForeignEtfEtnRate, DiffReitRate, DiffForeignReitRate, DiffBondRate, DiffForeignBondRate, DiffEbRate, DiffForeignEbRate, DiffRealEstateRate, DiffCashRate, DiffOtherRate, AssetFluctuationThresholdPer }, Articles { ContentUrl, ImageUrl, Title, Type }, PositionRanking { DecreasedPositionList { CashMargin, DiffValidationAmount, DiffValidationAmountUSD, DiffValidationRatio, ProductType, Side, Symbol, SymbolName, ValidationAmount, ValidationAmountUSD, CurrencyUnit }, IncreasedPositionList { CashMargin, DiffValidationAmount, DiffValidationAmountUSD, DiffValidationRatio, ProductType, Side, Symbol, SymbolName, ValidationAmount, ValidationAmountUSD, CurrencyUnit } } }, Year { CreatedAt, PreviousQuery, NextQuery, Base { From, To, TotalAssetAmount, PreviousTotalAssetAmount, DiffTotalAssetAmount, DiffTotalAssetRate, DiffStockRate, DiffMarginRate, DiffMarginProfitOrLossRate, DiffRoboAdvisorRate, DiffUSStockRate, DiffInvestmentTrustRate, DiffCashRate, DiffOtherRate, AssetFluctuationThresholdPer }, Articles { ContentUrl, ImageUrl, Title, Type }, PositionRanking { DecreasedPositionList { CashMargin, DiffValidationAmount, DiffValidationAmountUSD, DiffValidationRatio, ProductType, Side, Symbol, SymbolName, ValidationAmount, ValidationAmountUSD, CurrencyUnit }, IncreasedPositionList { CashMargin, DiffValidationAmount, DiffValidationAmountUSD, DiffValidationRatio, ProductType, Side, Symbol, SymbolName, ValidationAmount, ValidationAmountUSD, CurrencyUnit } } } } }`;

  const ret = await execute(req);
  return ret.GetReport as GetReportResult;
};

export type PrepareUserBrokerResult = {
  Success: boolean;
  OnetimeToken: string | null;
  ProvisionalID: string | null;
  Status: string | null;
};
type PrepareUserBrokerParam = {
  brokerID: string;
  account?: string;
};
export const prepareUserBroker = async (
  param: PrepareUserBrokerParam
): Promise<PrepareUserBrokerResult> => {
  const req = `mutation { PrepareUserBroker(input: { BrokerID: "${param.brokerID ??
    ''}", Account: "${param?.account ??
    ''}" } ) { Success, OnetimeToken, ProvisionalID, Status } }`;
  const ret = await execute(req);

  return ret.PrepareUserBroker as PrepareUserBrokerResult;
};

export type GetGoalResult = {
  ID: string;
  RefType: number;
  Title: string;
  ReserveAmount: number;
  TargetAmount: number;
  CurrentAmount: number | null;
  TargetYield: number;
  StartDate: string;
  EndDate: string;
  Achieved: boolean;
  LinkedAsset: boolean;
  AccountGroupIDList?: Array<string>;
};

export const getGoal = async (id?: string): Promise<GetGoalResult> => {
  const req = `{ GetGoal(id: "${id}") { ID, RefType, Title, ReserveAmount, TargetAmount, CurrentAmount, TargetYield, StartDate, EndDate, Achieved, LinkedAsset, AccountGroupIDList } }`;
  const ret = await execute(req);
  return ret.GetGoal as GetGoalResult;
};

export type GetGoalListResult = {
  Items: Array<GetGoalResult> | null;
};
export const getGoalList = async (): Promise<GetGoalListResult> => {
  const req = `{ GetGoalList { Items { ID, RefType, Title, ReserveAmount, TargetAmount, CurrentAmount, TargetYield, StartDate, EndDate, Achieved, LinkedAsset, AccountGroupIDList } } }`;
  const ret = await execute(req);
  return ret.GetGoalList as GetGoalListResult;
};

type GoalSubTotalData = {
  Year: string;
  Month: string;
  From: string;
  To: string;
  TargetAmount: number;
  AchievementAmount: number | null;
  Yield: number;
};
type GoalTotalData = {
  BenchmarkAmount: number;
  CurrentAmount: number;
  Yield: number;
};
type AdviceLink = {
  Label: string;
  URL: string;
};
type Advice = {
  Title: string;
  Comment: string;
  LinkList: Array<AdviceLink>;
};
export type GetGoalDetailResult = {
  ID: string;
  MonthList: Array<GoalSubTotalData>;
  YearList: Array<GoalSubTotalData>;
  All: GoalTotalData;
  AdviceList: Array<Advice>;
};
export const getGoalDetail = async (
  id: string
): Promise<GetGoalDetailResult> => {
  const req = `{ GetGoalDetail(id: "${id}") { ID, MonthList { Year, Month, From, To, TargetAmount, AchievementAmount, Yield }, YearList { Year, Month, From, To, TargetAmount, AchievementAmount, Yield }, All { BenchmarkAmount, CurrentAmount, Yield }, AdviceList { Title, Comment, LinkList { Label, URL } } } }`;
  const ret = await execute(req);
  return ret.GetGoalDetail as GetGoalDetailResult;
};

type GetGoalTransitionData = {
  Date: string;
  Amount: number;
};
export type GetGoalTransitionResult = {
  ID: string;
  BenchmarkList: Array<GetGoalTransitionData>;
  CurrentList: Array<GetGoalTransitionData>;
};
export const getGoalTransition = async (
  id: string
): Promise<GetGoalTransitionResult> => {
  const req = `{ GetGoalTransition(id: "${id}") { ID, BenchmarkList { Date, Amount }, CurrentList { Date, Amount } } }`;
  const ret = await execute(req);
  return ret.GetGoalTransition as GetGoalTransitionResult;
};

type RegisterGoalResult = {
  GoalID: string;
};
export const registerGoal = async (
  refType: number,
  title: string,
  reserveAmount: number,
  targetAmount: number,
  targetYield: number | null,
  startDate: string,
  endDate: string,
  linkedAsset: boolean,
  accountGroupIDList: Array<string>
): Promise<RegisterGoalResult> => {
  const req = `mutation { RegisterGoal(input: { RefType: "${refType}", Title: "${title}", ReserveAmount: ${reserveAmount}, TargetAmount: ${targetAmount}, TargetYield: ${targetYield}, StartDate: "${startDate}", EndDate: "${endDate}", LinkedAsset: ${linkedAsset}, AccountGroupIDList: [${accountGroupIDList.map(
    target => `"${target}"`
  )}] } ) { GoalID } }`;
  const ret = await execute(req);

  return ret.RegisterGoal as RegisterGoalResult;
};

export type UpdateGoalResult = {
  GoalID: string;
};
export const updateGoal = async (
  id: string,
  refType: number,
  title: string,
  reserveAmount: number,
  targetAmount: number,
  targetYield: number | null,
  startDate: string,
  endDate: string,
  linkedAsset: boolean,
  accountGroupIDList: Array<string>
): Promise<UpdateGoalResult> => {
  const req = `mutation { UpdateGoal(input: { ID: "${id}", RefType: "${refType}", Title: "${title}", ReserveAmount: ${reserveAmount}, TargetAmount: ${targetAmount}, TargetYield: ${targetYield}, StartDate: "${startDate}", EndDate: "${endDate}", LinkedAsset: ${linkedAsset}, AccountGroupIDList: [${accountGroupIDList.map(
    target => `"${target}"`
  )}] } ) { GoalID } }`;
  const ret = await execute(req);

  return ret.UpdateGoal as UpdateGoalResult;
};

type CompleteGoalResult = {
  GoalID: string;
};
export const completeGoal = async (id: string): Promise<CompleteGoalResult> => {
  const req = `mutation { CompleteGoal(input: { ID: "${id}" } ) { GoalID } }`;
  const ret = await execute(req);

  return ret.CompleteGoal as CompleteGoalResult;
};

export type GetStockRankingResult = {
  sync_on: string;
  target_on: string;
  stock_code: string;
  stock_name: string;
  prediction_category: string;
  prediction_rank: number;
  prev_prediction_rank: number | null;
  close_price: number;
  description: string;
  dividend_yield_forecast: number;
  yutai_present: string;
  last_year_roi: number;
  last_year_sharpe_ratio: number;
};
export const getStockRanking = async (
  roi: number
): Promise<Array<GetStockRankingResult>> => {
  const req = `{ GetStockRanking(roi: ${roi}) { sync_on, target_on, stock_code, stock_name, prediction_category, prediction_rank, prev_prediction_rank, close_price, description, dividend_yield_forecast, yutai_present, last_year_roi, last_year_sharpe_ratio } }`;
  const ret = await execute(req);
  return ret.GetStockRanking as Array<GetStockRankingResult>;
};
export type GetRankingResult = {
  target_on: string;
  symbol: string;
  name: string;
  prediction_category: string;
  prediction_rank: number;
  close_price: number;
  description: string;
  reference_roi: number;
  reference_sharpe_ratio: number;
  dividend_yield_forecast: number | null;
  yutai_present: string | null;
  ra_reward: number | null;
  id_largeclass: string | null;
  id_largeclass_name: string;
  id_investstylelarge: string | null;
  id_investstylelarge_name: string;
  ra_aveprofits: number | null;
};
export const getRanking = async (
  productType: string,
  roi: number
): Promise<Array<GetRankingResult>> => {
  const req = `{ GetRanking(productType: "${productType}", roi: ${roi}) { target_on, symbol, name, prediction_category, prediction_rank, close_price, description, reference_roi, reference_sharpe_ratio, dividend_yield_forecast, yutai_present, ra_reward, id_largeclass, id_largeclass_name, id_investstylelarge, id_investstylelarge_name, ra_aveprofits} }`;
  const ret = await execute(req);
  return ret.GetRanking as Array<GetRankingResult>;
};

export type GetManulAssetResult = {
  ID: string;
  AssetName: string;
  AssetType: number;
  AssetAmount: number;
};
export const getManualAsset = async (
  id: string
): Promise<GetManulAssetResult> => {
  const req = `{ GetManualAsset(id: "${id}") { ID, AssetName, AssetType, AssetAmount } }`;
  const ret = await execute(req);
  return ret.GetManualAsset as GetManulAssetResult;
};

export type GetManulAssetsResult = {
  Items: Array<GetManulAssetResult>;
};
export const getManualAssets = async (): Promise<GetManulAssetsResult> => {
  const req = `{ GetManualAssets { Items { ID, AssetName, AssetType, AssetAmount } } }`;
  const ret = await execute(req);
  return ret.GetManualAssets as GetManulAssetsResult;
};

export type CreateManulAssetResult = {
  ID: string;
};
export const createManualAsset = async (
  assetName: string,
  assetType: number,
  assetAmount: number
): Promise<CreateManulAssetResult> => {
  const req = `mutation { CreateManualAsset(input: { AssetName: "${assetName}", AssetType: ${assetType}, AssetAmount: ${assetAmount} } ) { ID } }`;
  const ret = await execute(req);
  return ret.CreateManualAsset as CreateManulAssetResult;
};

export type UpdateManulAssetResult = {
  ID: string;
};
export const updateManualAsset = async (
  id: string,
  assetName: string,
  assetType: number,
  assetAmount: number
): Promise<UpdateManulAssetResult> => {
  const req = `mutation { UpdateManualAsset(input: { ID: "${id}", AssetName: "${assetName}", AssetType: ${assetType}, AssetAmount: ${assetAmount} }) { ID } }`;
  const ret = await execute(req);
  return ret.UpdateManualAsset as UpdateManulAssetResult;
};

export type DeleteManulAssetResult = {
  ID: string;
};
export const deleteManualAsset = async (
  id: string
): Promise<DeleteManulAssetResult> => {
  const req = `mutation { DeleteManualAsset(input: { ID: "${id}" } ) { ID } }`;
  const ret = await execute(req);
  return ret.DeleteManualAsset as DeleteManulAssetResult;
};

type UnlinkMoneytreeResult = {
  Success: boolean;
};
export const unlinkMoneytree = async (): Promise<UnlinkMoneytreeResult> => {
  const req = `mutation { UnlinkMoneytree { Success } }`;
  const ret = await execute(req);

  return ret.UnlinkMoneytree as UnlinkMoneytreeResult;
};

type RefreshMoneytreeResult = {
  Success: boolean;
};
export const refreshMoneytree = async (): Promise<RefreshMoneytreeResult> => {
  const req = `mutation { RefreshMoneytree { Success } }`;
  const ret = await execute(req);

  return ret.RefreshMoneytree as RefreshMoneytreeResult;
};

export type UpdateAccountAvailableResult = {
  Success: boolean;
};
export const updateAccountAvailable = async (
  iDList: Array<string>
): Promise<UpdateAccountAvailableResult> => {
  const targets = iDList.map(target => `"${target}"`);

  const req = `mutation { UpdateAccountAvailable (input: { IDList: [${targets}] } ) { Success } }`;
  const ret = await execute(req);
  return ret.UpdateAccountAvailable as UpdateAccountAvailableResult;
};

export type GetAccountGroupItemResult = {
  ID: string;
  Name: string;
  Accounts: Array<string>;
  TargetGoalID?: string;
  TargetGoalRefType?: number;
  TargetGoalTitle?: string;
  InsTime: string;
  UpdTime?: string;
};
export type GetAccountGroupResult = {
  Items: Array<GetAccountGroupItemResult>;
};
export const getAccountGroup = async (): Promise<GetAccountGroupResult> => {
  const req = `{ GetAccountGroup { Items { ID, Name, Accounts, TargetGoalID, TargetGoalRefType, TargetGoalTitle, InsTime, UpdTime  } } }`;
  const ret = await execute(req);
  return ret.GetAccountGroup as GetAccountGroupResult;
};

export type OperateAccountGroupItemParam = {
  ID?: string;
  Name: string;
  Accounts: Array<string>;
};
export type OperateAccountGroupParam = {
  Items: Array<OperateAccountGroupItemParam>;
};
export type OperateAccountGroupResult = {
  Success: boolean;
};
export const operateAccountGroup = async (
  param: OperateAccountGroupParam
): Promise<OperateAccountGroupResult> => {
  const req = `mutation { OperateAccountGroup (input: ${stringifyWithoutQuoteOnKey(
    param
  )}) { Success } }`;
  const ret = await execute(req);
  return ret.OperateAccountGroup as OperateAccountGroupResult;
};

type RegisterPushTokenResult = {
  Success: boolean;
};
export const registerPushToken = async (
  token: string
): Promise<RegisterPushTokenResult> => {
  const req = `mutation { RegisterPushToken(input: { Token: "${token}" }) { Success } }`;
  const ret = await execute(req);
  return ret.RegisterPushToken as RegisterPushTokenResult;
};

export type GetIntegratedAssetResult = {
  TotalAsset: TotalAsset;
  GroupAssets: Array<GroupAsset>;
};

type GetAssetResultItemHistory = {
  Day: number;
  TotalAssetAmount: number | null;
  DiffTotalAssetAmount: number | null;
  DiffTotalAssetRate: number | null;
  MarginProfitOrLoss: number | null;
  TotalProfitOrLoss: number | null;
  DiffTotalProfitOrLoss: number | null;
  DiffTotalProfitOrLossRate: number | null;
  StockAmount: number | null;
  MarginAmount: number | null;
  RoboAdvisorAmount: number | null;
  USStockAmount: number | null;
  InvestmentTrustAmount: number | null;
  CashAmount: number | null;
  OtherAmount: number | null;
  Incorrect: boolean;
  Topix: number;
};

export type TotalAsset = {
  Asset: GetAssetResultItem;
  AssetHistory: Array<GetAssetResultItemHistory>;
  Goals: Array<ReportGoal>;
  Brokers: Array<Broker>;
  AssetFluctuation: AssetFluctuationDWM | null;
  Positions: Array<GetPositionsResultItem>;
};

export type GroupAsset = {
  ID: string;
  Name: string;
} & TotalAsset;

// 初代と統合するかも
export type AssetFluctuationDWM = {
  Day: Fluctuation;
  Week: Fluctuation;
  Month: Fluctuation;
};

export type Fluctuation = {
  Amount: number;
  Rate: number | null;
  From?: number;
  To?: number;
  ReportCreatedAt: string | null;
  ReportQuery: string | null;
};

export type Broker = {
  Account: string;
  Name: string;
  AggregateSource: number;
  Available: boolean;
  Status: string | null;
  TotalAssetAmount: number;
  CashAmount: number;
  DiffTotalAssetAmount: number;
  TotalProfitOrLoss: number;
  DiffTotalProfitOrLoss: number;
};

const getIntegratedAsset_ = async (): Promise<GetIntegratedAssetResult> => {
  const req = `{ GetIntegratedAsset {
                  TotalAsset {
                    Asset {
                      Day,
                      TotalAssetAmount,
                      TotalProfitOrLoss,
                      StockAmount,
                      MarginAmount,
                      RoboAdvisorAmount,
                      USStockAmount,
                      InvestmentTrustAmount,
                      CashAmount,
                      OtherAmount,
                      DiffTotalAssetAmount,
                      DiffTotalAssetRate,
                      MarginProfitOrLoss,
                      DiffTotalProfitOrLoss,
                      DiffTotalProfitOrLossRate
                    },
                    AssetHistory {
                      Day,
                      TotalAssetAmount,
                      DiffTotalAssetAmount,
                      DiffTotalAssetRate,
                      MarginProfitOrLoss,
                      TotalProfitOrLoss,
                      DiffTotalProfitOrLoss,
                      DiffTotalProfitOrLossRate,
                      StockAmount,
                      MarginAmount,
                      RoboAdvisorAmount,
                      USStockAmount,
                      InvestmentTrustAmount,
                      CashAmount,
                      OtherAmount,
                      Incorrect,
                      Topix,
                    },
                    Goals {
                      AchievementAmount,
                      ID,
                      RefType,
                      TargetAmount,
                      Title,
                      TotalTargetAmount
                    },
                    Brokers {
                      Account,
                      Name,
                      AggregateSource,
                      Available,
                      Status,
                      TotalAssetAmount,
                      CashAmount,
                      DiffTotalAssetAmount,
                      TotalProfitOrLoss,
                      DiffTotalProfitOrLoss
                    },
                    AssetFluctuation {
                      Day {
                        Amount,
                        Rate,
                        ReportCreatedAt,
                        ReportQuery
                      },
                      Week {
                        Amount,
                        Rate,
                        From,
                        To,
                        ReportCreatedAt,
                        ReportQuery
                      },
                      Month {
                        Amount,
                        Rate,
                        From,
                        To,
                        ReportCreatedAt,
                        ReportQuery
                      }
                    },
                    Positions {
                      BrokerID,
                      Account,
                      Symbol,
                      SymbolName,
                      AccountType,
                      ProductType,
                      CashMargin,
                      Side,
                      Qty,
                      Price,
                      PriceUSD,
                      CurrentAmount,
                      CurrentAmountUSD,
                      ProfitOrLoss,
                      ProfitOrLossUSD
                    }
                  },
                  GroupAssets {
                    ID,
                    Name,
                    Asset {
                      Day,
                      TotalAssetAmount,
                      TotalProfitOrLoss,
                      StockAmount,
                      MarginAmount,
                      RoboAdvisorAmount,
                      USStockAmount,
                      InvestmentTrustAmount,
                      CashAmount,
                      OtherAmount,
                      DiffTotalAssetAmount,
                      DiffTotalAssetRate,
                      DiffTotalProfitOrLoss,
                      DiffTotalProfitOrLossRate
                    },
                    AssetHistory {
                      Day,
                      TotalAssetAmount,
                      DiffTotalAssetAmount,
                      DiffTotalAssetRate,
                      TotalProfitOrLoss,
                      DiffTotalProfitOrLoss,
                      DiffTotalProfitOrLossRate,
                      StockAmount,
                      MarginAmount,
                      RoboAdvisorAmount,
                      USStockAmount,
                      InvestmentTrustAmount,
                      CashAmount,
                      OtherAmount,
                      Incorrect,
                      Topix,
                    },
                    Goals {
                      AchievementAmount,
                      ID,
                      RefType,
                      TargetAmount,
                      Title,
                      TotalTargetAmount
                    },
                    Brokers {
                      Account,
                      Name,
                      AggregateSource,
                      Available,
                      Status,
                      TotalAssetAmount,
                      CashAmount,
                      DiffTotalAssetAmount,
                      TotalProfitOrLoss,
                      DiffTotalProfitOrLoss
                    },
                    AssetFluctuation {
                      Day {
                        Amount,
                        Rate,
                        ReportCreatedAt,
                        ReportQuery
                      },
                      Week {
                        Amount,
                        Rate,
                        From,
                        To,
                        ReportCreatedAt,
                        ReportQuery
                      },
                      Month {
                        Amount,
                        Rate,
                        From,
                        To,
                        ReportCreatedAt,
                        ReportQuery
                      }
                    },
                    Positions {
                      BrokerID,
                      Account,
                      Symbol,
                      SymbolName,
                      AccountType,
                      ProductType,
                      CashMargin,
                      Side,
                      Qty,
                      Price,
                      PriceUSD,
                      CurrentAmount,
                      CurrentAmountUSD,
                      ProfitOrLoss,
                      ProfitOrLossUSD
                    }
                  }
                }
              }`;
  const ret = await execute(req);
  return ret.GetIntegratedAsset as GetIntegratedAssetResult;
};

export const getIntegratedAsset: typeof getIntegratedAsset_ = async () => {
  const ret = await getIntegratedAsset_();
  return ret;
};

type GetTransactionItemResult = {
  Account: string;
  BrokerName?: string;
  Category: string;
  CategoryText: string;
  Description: string;
  Amount: number;
  AmountUSD: number | null;
  DateTime: string;
};

export type GetTransactionResult = {
  Items: Array<GetTransactionItemResult>;
};

const getTransactions_ = async (
  account?: string,
  reportType?: number
): Promise<GetTransactionResult> => {
  const queryAcount = account ? `"${account}"` : null;
  const queryReportType = reportType ? `${reportType}` : null;

  const req = `{ GetTransactions(account: ${queryAcount}, reportType: ${queryReportType}) { Items { Account, BrokerName, Category, CategoryText, Description, Amount, AmountUSD, DateTime } } }`;
  const ret = await execute(req);
  return ret.GetTransactions as GetTransactionResult;
};
export const getTransactions: typeof getTransactions_ = async (
  account,
  reportType
) => {
  const ret = await getTransactions_(account, reportType);
  return ret;
};

type CurrencyPair = {
  ID: string;
  Name: string;
  Rate: number;
  RateDateTime: string;
};

export type GetCurrencyPairsResult = {
  Items: Array<CurrencyPair>;
};

const getCurrencyPairs_ = async (): Promise<GetCurrencyPairsResult> => {
  const req = `{ GetCurrencyPairs { Items { ID, Name, Rate, RateDateTime } } }`;
  const ret = await execute(req);
  return ret.GetCurrencyPairs as GetCurrencyPairsResult;
};
export const getCurrencyPairs: typeof getCurrencyPairs_ = async () => {
  const ret = await getCurrencyPairs_();
  return ret;
};

export type GetSNSAccountResult = {
  Items: {
    SNSType: number;
    Name: string | null;
    ProfileImageURL: string | null;
    LinkURL: string;
    Linked: boolean;
  }[];
} | null;

export const getSNSAccount = async (
  redirectPath: string
): Promise<GetSNSAccountResult> => {
  const req = `{ 
    GetSNSAccount(redirectPath: "${redirectPath}") { 
      Items { SNSType, Name, ProfileImageURL, LinkURL, Linked }
    } 
  }`;
  const ret = await execute(req);
  return (ret ? ret.GetSNSAccount : null) as GetSNSAccountResult;
};
type PostSNSImageResult = {
  Success: boolean;
};
type PostSNSImageParam = {
  SNSType: number;
  Text: string;
  ImageData: string;
};
export const postSNSImage = async (
  param: PostSNSImageParam
): Promise<PostSNSImageResult> => {
  const jsonString = stringifyWithoutQuoteOnKey(param);
  const req = `mutation { PostSNSImage(input: ${jsonString} ) { Success } }`;
  const ret = await execute(req);

  return ret.PostSNSImage as PostSNSImageResult;
};

type UnlinkSNSAccountResult = {
  Success: boolean;
};
type UnlinkSNSAccountParam = {
  SNSType: number;
};
export const unlinkSNSAccount = async (
  param: UnlinkSNSAccountParam
): Promise<UnlinkSNSAccountResult> => {
  const jsonString = stringifyWithoutQuoteOnKey(param);
  const req = `mutation { UnlinkSNSAccount(input: ${jsonString} ) { Success } }`;
  const ret = await execute(req);

  return ret.UnlinkSNSAccount as UnlinkSNSAccountResult;
};

export type GetTargetDetailResult = {
  ID: string;
  Title: string;
  RefType: number;
  StartDate: string;
  EndDate: string;
  TargetAmount: number;
  AccountList: Array<
    Pick<GetUserBrokerResultItem, 'Account'> & {
      BrokerName: string;
      Available: boolean;
      TotalAssetAmount: number;
    }
  >;
  ExcludeAmount: number;
  ReserveAmount: number;
  AssetAmount: number;
  Yield: number;
  Achieved: boolean;
  Status: number;
};

const getTargetDetail_ = async (id: string): Promise<GetTargetDetailResult> => {
  const req = `{ GetTargetDetail(id: "${id}") { 
                  ID, 
                  Title,
                  RefType,
                  StartDate,
                  EndDate,
                  TargetAmount,
                  AccountList {
                    Account, 
                    BrokerName, 
                    Available, 
                    TotalAssetAmount
                  },
                  ExcludeAmount,
                  ReserveAmount,
                  AssetAmount,
                  Yield,
                  Achieved,
                  Status
                }         
              }`;
  const ret = await execute(req);
  return ret.GetTargetDetail as GetTargetDetailResult;
};
export const getTargetDetail: typeof getTargetDetail_ = async id => {
  const ret = await getTargetDetail_(id);
  return ret;
};

export type GetTargetListResult = {
  Items: Array<GetTargetDetailResult> | null;
};
const getTargetList_ = async (): Promise<GetTargetListResult> => {
  const req = `{ GetTargetList {
                  Items { 
                    ID, 
                    Title,
                    RefType,
                    StartDate,
                    EndDate,
                    TargetAmount,
                    AccountList {
                      Account, 
                      BrokerName, 
                      Available, 
                      TotalAssetAmount
                    },
                    ExcludeAmount,
                    ReserveAmount,
                    AssetAmount,
                    Yield,
                    Achieved,
                    Status
                  }
                }           
              }`;
  const ret = await execute(req);
  return ret.GetTargetList as GetTargetListResult;
};
export const getTargetList: typeof getTargetList_ = async () => {
  const ret = await getTargetList_();
  return ret;
};

type GetTargetHistoryTransitionData = {
  AssetAmount: number;
  BenchmarkAmount: number;
  Date: string;
  Incorrect: boolean;
};

export type GetTargetHistoryResult = {
  ID: string;
  AchievementAmount: number;
  TargetAmount: number;
  From: string;
  To: string;
  Transitions: Array<GetTargetHistoryTransitionData>;
};

const getTargetHistory_ = async (
  id: string,
  period: string
): Promise<GetTargetHistoryResult> => {
  const req = `{ GetTargetHistory(
                  id: "${id}", 
                  period: "${period}") { 
                    ID, 
                    AchievementAmount,
                    TargetAmount,
                    From,
                    To,
                    Transitions { 
                      AssetAmount,
                      BenchmarkAmount,
                      Date,
                      Incorrect
                    }
                  } 
                }`;
  const ret = await execute(req);
  return ret.GetTargetHistory as GetTargetHistoryResult;
};
export const getTargetHistory: typeof getTargetHistory_ = async (...arg) => {
  const ret = await getTargetHistory_(...arg);
  return ret;
};

export type AssetTransitionItem = {
  AssetAmount: number;
  DateYM: string;
};

export type EvaluateAssetManagementResult = {
  Yield: number | null;
  SuggestionLimitPer: number;
  HighRiskPer: number;
  AssetTransitions: Array<AssetTransitionItem>;
} | null;

const evaluateAssetManagement_ = async (
  id: string,
  accountIDList: string[] | null,
  excludeAmount: number,
  reserveAmount: number,
  targetAmount?: number | null,
  endDate?: string | null
): Promise<EvaluateAssetManagementResult> => {
  const req = `{ EvaluateAssetManagement(
                  id: "${id}",
                  accountIDList: [${
                    accountIDList?.length
                      ? `${accountIDList?.map(x => `"${x}"`)}`
                      : ''
                  }],
                  excludeAmount: ${excludeAmount},
                  reserveAmount: ${reserveAmount},
                  targetAmount: ${targetAmount},
                  endDate: "${endDate}") {
                    Yield,
                    SuggestionLimitPer,
                    HighRiskPer,
                    AssetTransitions {
                      AssetAmount,
                      DateYM
                    }
                  }
                }`;
  const ret = await execute(req);
  return ret.EvaluateAssetManagement as EvaluateAssetManagementResult;
};
export const evaluateAssetManagement: typeof evaluateAssetManagement_ = async (
  id,
  accountIDList,
  excludeAmount,
  reserveAmount,
  targetAmount,
  endDate
) => {
  const ret = await evaluateAssetManagement_(
    id,
    accountIDList,
    excludeAmount,
    reserveAmount,
    targetAmount,
    endDate
  );
  return ret;
};

export type RegisterTargetRequest = {
  Title: string;
  RefType: number;
  StartDate: string;
  EndDate: string;
  TargetAmount: number;
};
type RegisterTargetResult = {
  ID: string;
  ResultCode: number;
};

const registerTarget_ = async (
  param: RegisterTargetRequest
): Promise<RegisterTargetResult> => {
  const req = `mutation { RegisterTarget(input: ${stringifyWithoutQuoteOnKey(
    param
  )} ) { ID, ResultCode } }`;
  const ret = await execute(req);

  return ret.RegisterTarget as RegisterTargetResult;
};
export const registerTarget: typeof registerTarget_ = async arg => {
  const ret = await registerTarget_(arg);
  return ret;
};

export type UpdateTargetRequest = {
  ID: string;
  Title: string;
  StartDate: string;
  EndDate: string;
  TargetAmount: number;
  TargetYield?: number | null;
  RefType: number;
  AccountIDList?: Array<string>;
  ExcludeAmount?: number;
  ReserveAmount?: number;
  ManagementType?: number;
};
export type UpdateTargetResult = {
  ResultCode: number;
};

const updateTarget_ = async (
  param: UpdateTargetRequest
): Promise<UpdateTargetResult> => {
  const req = `mutation { UpdateTarget(input: ${stringifyWithoutQuoteOnKey(
    param
  )} ) { ResultCode } }`;
  const ret = await execute(req);

  return ret.UpdateTarget as UpdateTargetResult;
};
export const updateTarget: typeof updateTarget_ = async arg => {
  const ret = await updateTarget_(arg);
  return ret;
};

type CompleteTargetResult = {
  ResultCode: number;
};

const completeTarget_ = async (id: string): Promise<CompleteTargetResult> => {
  const req = `mutation { CompleteTarget(input: { ID: "${id}" } ) { ResultCode } }`;
  const ret = await execute(req);
  return ret.CompleteTarget as CompleteTargetResult;
};
export const completeTarget: typeof completeTarget_ = async id => {
  const ret = await completeTarget_(id);
  return ret;
};

export type PortfolioAnalysisItem = {
  AssetType: string;
  CurrentPer: number;
  TargetPer: number;
};
export type ManagementItem = {
  ManagementType: number;
  ItemsByPortfolioAnalysis: Array<PortfolioAnalysisItem>;
};

export type GetPortfolioAnalysisResult = {
  ItemsByPortfolioAnalysis: Array<PortfolioAnalysisItem>;
  RegisteredAccountName: Array<string>;
  TargetReturnPer: number;
  TargetReturnLimitPer: number;
  UpdateDateOfAsset: string;
  ManagementType: number;
  ManagementItems: Array<ManagementItem>;
};

const getPortfolioAnalysis_ = async (
  targetId: string
): Promise<GetPortfolioAnalysisResult> => {
  const req = `{ GetPortfolioAnalysis(targetId: "${targetId}") {
                  ItemsByPortfolioAnalysis { 
                    AssetType, 
                    CurrentPer,
                    TargetPer
                  },
                  RegisteredAccountName,
                  TargetReturnPer,
                  TargetReturnLimitPer,
                  UpdateDateOfAsset,
                  ManagementType,
                  ManagementItems {
                    ManagementType,
                    ItemsByPortfolioAnalysis { 
                      AssetType, 
                      CurrentPer,
                      TargetPer
                    }
                  }
                }           
              }`;
  const ret = await execute(req);

  return ret.GetPortfolioAnalysis as GetPortfolioAnalysisResult;
};
export const getPortfolioAnalysis: typeof getPortfolioAnalysis_ = async targetId => {
  const ret = await getPortfolioAnalysis_(targetId);
  return ret;
};

export type InvestmentTrustFundByAssetItem = {
  InvestmentType: string;
  Held: boolean;
  OneYearReturnPer: number | null;
  SalesCompany: Array<string>;
  StandardPrice: number;
  StandardPriceDate: string;
  Symbol: string;
  SymbolName: string;
  TrustFeePer: number;
};

export type GetInvestmentTrustFundByAssetResult = {
  ItemsByInvestmentType: Array<InvestmentTrustFundByAssetItem>;
  AssetType: string;
  BenchMark: string;
  CurrentPer: number;
  DissociatePer: number;
  TargetPer: number;
  TargetPurchaseAmount: number;
};

const getInvestmentTrustFundByAsset_ = async (
  targetId: string,
  assetType: string
): Promise<GetInvestmentTrustFundByAssetResult> => {
  const req = `{ GetInvestmentTrustFundByAsset(targetId: "${targetId}", assetType: "${assetType}") {
                  ItemsByInvestmentType { 
                    InvestmentType, 
                    Held,
                    OneYearReturnPer,
                    SalesCompany,
                    StandardPrice,
                    StandardPriceDate,
                    Symbol,
                    SymbolName,
                    TrustFeePer
                  },
                  AssetType,
                  BenchMark,
                  CurrentPer,
                  DissociatePer,
                  TargetPer,
                  TargetPurchaseAmount
                }           
              }`;
  const ret = await execute(req);

  return ret.GetInvestmentTrustFundByAsset as GetInvestmentTrustFundByAssetResult;
};
export const getInvestmentTrustFundByAsset: typeof getInvestmentTrustFundByAsset_ = async (
  targetId,
  assetType
) => {
  const ret = await getInvestmentTrustFundByAsset_(targetId, assetType);
  return ret;
};

export type GetNotificationSettingResult = {
  TimelyDisclosureBySymbol: Array<string>;
  AssetFluctuationThresholdPer: number;
  EconomicIndicatorsOnToday: boolean;
  NewDepositAndWithdrawal: boolean;
  NewTrade: boolean;
};

const getNotificationSetting_ = async (): Promise<GetNotificationSettingResult> => {
  const req = `{ GetNotificationSetting {
                  TimelyDisclosureBySymbol,
                  AssetFluctuationThresholdPer,
                  EconomicIndicatorsOnToday,
                  NewDepositAndWithdrawal,
                  NewTrade
                }           
              }`;
  const ret = await execute(req);

  return ret.GetNotificationSetting as GetNotificationSettingResult;
};
export const getNotificationSetting: typeof getNotificationSetting_ = async () => {
  const ret = await getNotificationSetting_();
  return ret;
};

export type UpdateNotificationSetting = {
  TimelyDisclosureBySymbol: Array<string>;
  AssetFluctuationThresholdPer: number;
  EconomicIndicatorsOnToday: boolean;
  NewDepositAndWithdrawal: boolean;
  NewTrade: boolean;
};
export type UpdateNotificationSettingResult = {
  ResultCode: number;
};

const updateNotificationSetting_ = async (
  param: UpdateNotificationSetting
): Promise<UpdateNotificationSettingResult> => {
  const req = `mutation { UpdateNotificationSetting(input: ${stringifyWithoutQuoteOnKey(
    param
  )} ) { ResultCode } }`;
  const ret = await execute(req);

  return ret.UpdateNotificationSetting as UpdateNotificationSettingResult;
};
export const updateNotificationSetting: typeof updateNotificationSetting_ = async arg => {
  const ret = await updateNotificationSetting_(arg);
  return ret;
};

type SyncAccountAssetResult = {
  ResultCode: number; // 0 ~ 5
};
export const syncAccountAsset = async (
  account?: string
): Promise<SyncAccountAssetResult> => {
  const queryAccount = account ? `"${account}"` : null;
  const req = `mutation { SyncAccountAsset(input: { Account: ${queryAccount} }) { ResultCode } }`;
  const ret = await execute(req);
  return ret.SyncAccountAsset as SyncAccountAssetResult;
};

export type GetUserTagsResult = {
  AgeTag?: number | null;
  AssetTag?: number | null;
  AnnualIncomeTag?: number | null;
  InvestingYearsTag?: number | null;
  QueryTags?: number[] | null;
};
export const getUserTags = async (): Promise<GetUserTagsResult> => {
  const req = `{ GetUserTags { AgeTag, AssetTag, AnnualIncomeTag, InvestingYearsTag, QueryTags } }`;
  const ret = await execute(req);
  return ret.GetUserTags as GetUserTagsResult;
};

export type GetPersonaAssetTransitionRequest = {
  AgeTag?: number | null;
  AssetTag?: number | null;
  QueryTags?: number[] | null;
  From?: string; // yyyy/MM/dd
  To?: string | null; // yyyy/MM/dd
};
export type PersonaAssetTransitionItem = {
  Date: string; // yyyy/MM/dd
  UserChangeRatio: number;
  AverageChangeRatio: number | null;
  BenchmarkAmount: number;
};
export type GetPersonaAssetTransitionResult = {
  Transitions: Array<PersonaAssetTransitionItem>;
};
export const getPersonaAssetTransition = async (
  param: GetPersonaAssetTransitionRequest
): Promise<GetPersonaAssetTransitionResult> => {
  const req = `{ GetPersonaAssetTransition(input: ${stringifyWithoutQuoteOnKey(
    param
  )}) { Transitions { Date, UserChangeRatio, AverageChangeRatio, BenchmarkAmount } } }`;
  const ret = await execute(req);
  return ret.GetPersonaAssetTransition as GetPersonaAssetTransitionResult;
};

export type GetPersonaAssetDistributionRequest = {
  MaxAssetM?: number | null; // 10, 50, 100:100万単位 未満まで、指定なし:すべて
  AgeTag?: number | null;
  QueryTags?: number[] | null;
};
export type PersonaAssetDistributionItem = {
  RangeFrom: number; // 階級値 以上
  RangeTo: number; // 階級値 未満、null: RangeFrom以上
  UserRatio: number;
  UserFlag: boolean;
  TargetFlag: boolean;
};
export type GetPersonaAssetDistributionResult = {
  TotalAssetAmount: number;
  MedianTotalAssetAmount: number | null;
  Distributions: Array<PersonaAssetDistributionItem>;
};
export const getPersonaAssetDistribution = async (
  param: GetPersonaAssetDistributionRequest
): Promise<GetPersonaAssetDistributionResult> => {
  const req = `{ GetPersonaAssetDistribution(input: ${stringifyWithoutQuoteOnKey(
    param
  )}) { TotalAssetAmount, MedianTotalAssetAmount, Distributions { RangeFrom, RangeTo, UserRatio, UserFlag, TargetFlag } } }`;
  const ret = await execute(req);
  return ret.GetPersonaAssetDistribution as GetPersonaAssetDistributionResult;
};

export type DebtAssetAccountLists = {
  Account: string;
  AccountName: string;
  RealDebtAmount: number;
};

type DebtAssetHistoriesList = {
  Day: number;
  DebtAmount: number;
};
export type DebtAssetAccountHistories = {
  Account: string;
  AccountName: string;
  Histories: Array<DebtAssetHistoriesList>;
};

export type DebtAssetGroupAssets = {
  ID: string;
  Name: string;
  AccountLists: Array<DebtAssetAccountLists>;
  AccountHistories: Array<DebtAssetAccountHistories>;
};

export type GetDebtAssetResult = {
  TotalAsset: {
    AccountLists: Array<DebtAssetAccountLists>;
    AccountHistories: Array<DebtAssetAccountHistories>;
  };
  GroupAssets: Array<DebtAssetGroupAssets>;
};
const getDebtAsset_ = async (): Promise<GetDebtAssetResult> => {
  const req = `{ GetDebtAsset { TotalAsset { AccountLists { Account, AccountName, RealDebtAmount }, AccountHistories { Account, AccountName, Histories { Day, DebtAmount } } }, GroupAssets { ID, Name, AccountLists { Account, AccountName, RealDebtAmount }, AccountHistories { Account, AccountName, Histories { Day, DebtAmount } } } } }`;
  const ret = await execute(req);

  return ret.GetDebtAsset as GetDebtAssetResult;
};

export const getDebtAsset: typeof getDebtAsset_ = async () => {
  const ret = await getDebtAsset_();

  return ret;
};

export type TotalAssetHistoriesItem = {
  Day: number;
  TotalAssetAmount: number;
};
export type DebtAssetHistoriesItem = {
  Day: number;
  DebtAmount: number;
};
export type GetAccountHistoryResult = {
  TotalAssetHistories: Array<TotalAssetHistoriesItem>;
  DebtAssetHistories: Array<DebtAssetHistoriesItem>;
};
export const getAccountHistory = async (
  account: string
): Promise<GetAccountHistoryResult> => {
  const req = `{ GetAccountHistory(account: "${account}") { TotalAssetHistories { Day, TotalAssetAmount }, DebtAssetHistories { Day, DebtAmount } } }`;
  const ret = await execute(req);
  return ret.GetAccountHistory as GetAccountHistoryResult;
};

export type AssetClassificationTotalAsset = {
  JP_EQ: number;
  DC_EQ: number;
  EM_EQ: number;
  JP_FI: number;
  DC_FI: number;
  EM_FI: number;
  JP_REIT: number;
  G_REIT: number;
  CASH: number;
  OTHER: number;
};

export type AssetClassificationGroupAssets = {
  ID: string;
  Name: string;
  // 必須じゃないかも
  JP_EQ: number;
  DC_EQ: number;
  EM_EQ: number;
  JP_FI: number;
  DC_FI: number;
  EM_FI: number;
  JP_REIT: number;
  G_REIT: number;
  CASH: number;
  OTHER: number;
};

export type GetAssetClassificationResult = {
  TotalAsset: AssetClassificationTotalAsset;
  GroupAssets: Array<AssetClassificationGroupAssets>;
};
const getAssetClassification_ = async (): Promise<GetAssetClassificationResult> => {
  const req = `{ GetAssetClassification { TotalAsset { JP_EQ, DC_EQ, EM_EQ, JP_FI, DC_FI, EM_FI, JP_REIT, G_REIT, CASH, OTHER }, GroupAssets { ID, Name, JP_EQ, DC_EQ, EM_EQ, JP_FI, DC_FI, EM_FI, JP_REIT, G_REIT, CASH, OTHER  } } }`;
  const ret = await execute(req);

  return ret.GetAssetClassification as GetAssetClassificationResult;
};

export const getAssetClassification: typeof getAssetClassification_ = async () => {
  const ret = await getAssetClassification_();
  return ret;
};

type AccountClassificationPositions = {
  Account: string;
  AccountType: number;
  BrokerID: string;
  BrokerName: string;
  CashMargin: number;
  CurrentAmount: number | null;
  CurrentAmountUSD: number | null;
  Price: number | null;
  PriceUSD: number | null;
  ProductType: number | null;
  ProfitOrLoss: number | null;
  ProfitOrLossUSD: number | null;
  Qty: number | null;
  Side: number;
  Symbol: string;
  SymbolName: string;
};

export type GetAccountClassificationResult = {
  TotalAsset: {
    General: number | null;
    Identification: number | null;
    Nisa: number | null;
    DefinedContributionPension: number | null;
    Other: number | null;
    Positions: Array<AccountClassificationPositions>;
  };
  GroupAssets: {
    ID: string;
    Name: string;
    General: number | null;
    Identification: number | null;
    Nisa: number | null;
    DefinedContributionPension: number | null;
    Other: number | null;
    Positions: Array<AccountClassificationPositions>;
  }[];
};
const getAccountClassification_ = async (): Promise<GetAccountClassificationResult> => {
  const req = `{ GetAccountClassification { TotalAsset { General, Identification, Nisa, DefinedContributionPension, Other, Positions { Account, AccountType, BrokerID, BrokerName, CashMargin, CurrentAmount, CurrentAmountUSD, Price, PriceUSD, ProductType, ProfitOrLoss, ProfitOrLossUSD, Qty, Side, Symbol, SymbolName } }, GroupAssets { ID, Name, General, Identification, Nisa, DefinedContributionPension, Other, Positions { Account, AccountType, BrokerID, BrokerName, CashMargin, CurrentAmount, CurrentAmountUSD, Price, PriceUSD, ProductType, ProfitOrLoss, ProfitOrLossUSD, Qty, Side, Symbol, SymbolName } } } }`;
  const ret = await execute(req);

  return ret.GetAccountClassification as GetAccountClassificationResult;
};

export const getAccountClassification: typeof getAccountClassification_ = async () => {
  const ret = await getAccountClassification_();

  return ret;
};

export type GetUserAssetResult = {
  Day: number;
  TotalAssetAmount: number | null;
  DiffTotalAssetAmount: number | null;
  DiffTotalAssetRate: number | null;
  MarginProfitOrLoss: number | null;
  TotalProfitOrLoss: number | null;
  DiffTotalProfitOrLoss: number | null;
  DiffTotalProfitOrLossRate: number | null;
  StockAmount: number | null;
  MarginAmount: number | null;
  RoboAdvisorAmount: number | null;
  USStockAmount: number | null;
  InvestmentTrustAmount: number | null;
  CashAmount: number | null;
  OtherAmount: number | null;
  UnlistedStockAmount: number | null;
  EtfEtnAmount: number | null;
  ForeignEtfEtnAmount: number | null;
  ReitAmount: number | null;
  ForeignReitAmount: number | null;
  BondAmount: number | null;
  ForeignBondAmount: number | null;
  EbAmount: number | null;
  ForeignEbAmount: number | null;
  RealEstateAmount: number | null;
};
export const getUserAsset = async (
  groupID?: string
): Promise<GetUserAssetResult> => {
  const queryGroupID = groupID ? `"${groupID}"` : null;
  const req = `{ GetUserAsset(groupID: ${queryGroupID}) { Day, TotalAssetAmount, DiffTotalAssetAmount, DiffTotalAssetRate, MarginProfitOrLoss, TotalProfitOrLoss, DiffTotalProfitOrLoss, DiffTotalProfitOrLossRate, StockAmount, MarginAmount, RoboAdvisorAmount, USStockAmount, InvestmentTrustAmount, UnlistedStockAmount, EtfEtnAmount, ForeignEtfEtnAmount, ReitAmount, ForeignReitAmount, BondAmount, ForeignBondAmount, EbAmount, ForeignEbAmount, RealEstateAmount, CashAmount, OtherAmount } }`;

  const ret = await execute(req);
  return ret.GetUserAsset as GetUserAssetResult;
};

export type GetAssetHistoryItemResult = {
  Day: number;
  TotalAssetAmount: number | null;
  DiffTotalAssetAmount: number | null;
  DiffTotalAssetRate: number | null;
  MarginProfitOrLoss: number | null;
  TotalProfitOrLoss: number | null;
  DiffTotalProfitOrLoss: number | null;
  DiffTotalProfitOrLossRate: number | null;
  StockAmount: number | null;
  MarginAmount: number | null;
  RoboAdvisorAmount: number | null;
  USStockAmount: number | null;
  InvestmentTrustAmount: number | null;
  UnlistedStockAmount: number | null;
  EtfEtnAmount: number | null;
  ForeignEtfEtnAmount: number | null;
  ReitAmount: number | null;
  ForeignReitAmount: number | null;
  BondAmount: number | null;
  ForeignBondAmount: number | null;
  EbAmount: number | null;
  ForeignEbAmount: number | null;
  RealEstateAmount: number | null;
  CashAmount: number | null;
  OtherAmount: number | null;
  Incorrect: boolean;
  Topix?: number | null;
};

export type GetAssetHistoryResult = {
  Items: Array<GetAssetHistoryItemResult>;
};

export const getAssetHistory = async (
  from?: string, // yyyy/MM/dd
  to?: string, // yyyy/MM/dd
  groupID?: string,
  withTopix?: boolean,
  reportType?: number
): Promise<GetAssetHistoryResult> => {
  const queryFrom = from ? `"${from}"` : null;
  const queryTo = to ? `"${to}"` : null;
  const queryGroupID = groupID ? `"${groupID}"` : null;
  const queryWithTopix = withTopix ? `${withTopix}` : null;
  const queryReportType = reportType ? `${reportType}` : null;

  const req = `{ GetAssetHistory(from: ${queryFrom}, to: ${queryTo}, groupID: ${queryGroupID}, withTopix: ${queryWithTopix}, reportType: ${queryReportType}) { Items { Day, TotalAssetAmount, DiffTotalAssetAmount, DiffTotalAssetRate, MarginProfitOrLoss, TotalProfitOrLoss, DiffTotalProfitOrLoss, DiffTotalProfitOrLossRate, StockAmount, MarginAmount, RoboAdvisorAmount, USStockAmount, InvestmentTrustAmount, UnlistedStockAmount, EtfEtnAmount, ForeignEtfEtnAmount, ReitAmount, ForeignReitAmount, BondAmount, ForeignBondAmount, EbAmount, ForeignEbAmount, RealEstateAmount, CashAmount, OtherAmount, Incorrect, Topix } } }`;
  const ret = await execute(req);
  return ret.GetAssetHistory as GetAssetHistoryResult;
};

export type GetIndexesItemResult = {
  Day: number;
  Mine: ReportIndexPrice | null;
  NK225: ReportIndexPrice | null;
  TOPIX: ReportIndexPrice | null;
  DJI: ReportIndexPrice | null;
  SSI: ReportIndexPrice | null;
  USDJPY: ReportIndexPrice | null;
  BTCJPY: ReportIndexPrice | null;
};

export type GetIndexesResult = {
  Items: Array<GetIndexesItemResult>;
};

export const getIndexes = async (
  from: string, // yyyy/MM/dd
  to: string // yyyy/MM/dd
): Promise<GetIndexesResult> => {
  const req = `{ GetIndexes(from: "${from}", to: "${to}") { Items { Day, Mine { Amount, Rate, DateProperty,Incorrect }, NK225 { Amount, Rate, DateProperty,Incorrect }, TOPIX { Amount, Rate, DateProperty,Incorrect }, DJI { Amount, Rate, DateProperty,Incorrect }, SSI { Amount, Rate, DateProperty,Incorrect }, USDJPY { Amount, Rate, DateProperty,Incorrect }, BTCJPY { Amount, Rate, DateProperty,Incorrect } } } }`;
  const ret = await execute(req);
  return ret.GetIndexes as GetIndexesResult;
};

export type GetPeriodGoalItemResult = {
  ID: string;
  RefType: number;
  Title: string;
  TotalTargetAmount: number;
  TargetAmount: number | null;
  AchievementAmount: number | null;
  Completed: boolean;
};

export type GetPeriodGoalResult = {
  Items: Array<GetPeriodGoalItemResult>;
};

export const getPeriodGoal = async (
  from?: string, // yyyy/MM/dd
  to?: string // yyyy/MM/dd
): Promise<GetPeriodGoalResult> => {
  const queryFrom = from ? `"${from}"` : null;
  const queryTo = to ? `"${to}"` : null;

  const req = `{ GetPeriodGoal(from: ${queryFrom}, to: ${queryTo}) { Items { ID, RefType, Title, TotalTargetAmount, TargetAmount, AchievementAmount, Completed } } }`;
  const ret = await execute(req);
  return ret.GetPeriodGoal as GetPeriodGoalResult;
};

type SendInvestorProvisionalRegistrationEmailResult = {
  ResultCode: number;
  MailToken: string;
};
type SendInvestorProvisionalRegistrationEmailParam = {
  email: string;
  redirectPath: string;
};
export const sendInvestorProvisionalRegistrationEmail = async (
  param: SendInvestorProvisionalRegistrationEmailParam
): Promise<SendInvestorProvisionalRegistrationEmailResult> => {
  const req = `mutation { SendInvestorProvisionalRegistrationEmail(email:"${param.email}", redirectPath: "${param.redirectPath}") { ResultCode, MailToken } }`;
  const ret = await execute(req);

  return ret.SendInvestorProvisionalRegistrationEmail as SendInvestorProvisionalRegistrationEmailResult;
};

export type GetUserAssetInfoResult = {
  Assets: number | null;
  UserBrokers: Array<string> | null;
  AccountTypes: Array<number> | null;
  Positions: Array<number> | null;
  Goals: Array<number> | null;
};
export const getUserAssetInfo = async (): Promise<GetUserAssetInfoResult> => {
  const req = `{ GetUserAssetInfo { Assets, UserBrokers, AccountTypes, Positions, Goals } }`;
  const ret = await execute(req);
  return ret.GetUserAssetInfo as GetUserAssetInfoResult;
};

export type GetHaitoItemResult = {
  Symbol: string;
  SymbolName: string;
  Haito: number;
  TaxExcludedHaito: number;
  Qty: number;
  AccountType: number;
  HaitoYm: string; // yyyy.mm
  Unreleased: boolean;
  HasNew: boolean;
};

export type GetHaitoResult = {
  Items: Array<GetHaitoItemResult>;
};

export const getHaito = async (
  date?: string // yyyymmdd
): Promise<GetHaitoResult> => {
  const target = date ? `"${date}"` : null;

  const req = `{ GetHaito(target: ${target}) { Items { Symbol, SymbolName, Haito, TaxExcludedHaito, Qty, AccountType, HaitoYm, Unreleased, HasNew } } }`;
  const ret = await execute(req);
  return ret.GetHaito as GetHaitoResult;
};

export type GetSymbolHaitoInfoResult = {
  KessanYm: string; // yyyy.mm
  Yield: number;
  YearlyAmount: number;
  CutOffMonth: string;
  DepositMonth: string;
};

export const getSymbolHaitoInfo = async (
  symbol: string
): Promise<GetSymbolHaitoInfoResult> => {
  const req = `{ GetSymbolHaitoInfo(symbol: "${symbol}") { KessanYm, Yield, YearlyAmount, CutOffMonth, DepositMonth } }`;
  const ret = await execute(req);
  return ret.GetSymbolHaitoInfo as GetSymbolHaitoInfoResult;
};

export type GetIncomeGainItemResult = {
  Account: string;
  BrokerID: string;
  BrokerName: string;
  Symbol: string;
  SymbolName: string;
  ProductType: number;
  Amount: number | null;
  Date: string; // yyyy/MM/dd
  ISIN: string;
  HasNew: boolean;
};
export type GetSymbolIncomeGainResult = {
  Items: Array<GetIncomeGainItemResult>;
};
export const getSymbolIncomeGain = async (
  symbol: string,
  from: string, // yyyyMM
  to: string // yyyyMM
): Promise<GetSymbolIncomeGainResult> => {
  const req = `{ GetSymbolIncomeGain(symbol: "${symbol}", from: "${from}", to: "${to}") { Items { Account, BrokerID, BrokerName, Symbol, SymbolName, ProductType, Amount, Date, ISIN, HasNew  } } }`;
  const ret = await execute(req);
  return ret.GetSymbolIncomeGain as GetSymbolIncomeGainResult;
};

export type GetIncomeGainResult = {
  From: string; // yyyyMM
  To: string; // yyyyMM
  Items: Array<GetIncomeGainItemResult>;
};
export const getIncomeGain = async (
  from?: string | null, // yyyyMM
  to?: string | null // yyyyMM
): Promise<GetIncomeGainResult> => {
  const queryFrom = from ? `"${from}"` : null;
  const queryTo = from ? `"${to}"` : null;
  const req = `{ GetIncomeGain(from: ${queryFrom}, to: ${queryTo}) { From, To, Items { Account, BrokerID, BrokerName, Symbol, SymbolName, ProductType, Amount, Date, ISIN, HasNew } } }`;
  const ret = await execute(req);
  return ret.GetIncomeGain as GetIncomeGainResult;
};

export type GetSymbolYutaiInfoResult = {
  YutaiTitle: string | null;
  YutaiYield: number | null;
};
export const getSymbolYutaiInfo = async (
  symbol: string
): Promise<GetSymbolYutaiInfoResult> => {
  const req = `{ GetSymbolYutaiInfo(symbol: "${symbol}") { YutaiTitle, YutaiYield } }`;
  const ret = await execute(req);
  return ret.GetSymbolYutaiInfo as GetSymbolYutaiInfoResult;
};

export type GetAdvertisementItemResult = {
  ID: string;
  Category: string;
  Title: string;
  Content: string;
  ImageUrl: string;
  ContentUrl: string;
  ButtonTitle: string;
};
export type GetAdvertisementResult = {
  Items: Array<GetAdvertisementItemResult>;
};
export const getAdvertisement = async (): Promise<GetAdvertisementResult> => {
  const req = `{ GetAdvertisement { Items { ID, Title, Category, Content, ImageUrl, ContentUrl, ButtonTitle } } }`;
  const ret = await execute(req);
  return ret.GetAdvertisement as GetAdvertisementResult;
};

type RegisterUserWebPushInfoParam = {
  endPoint: string;
  publicKey: string;
  authInfo: string;
};
type RegisterUserWebPushInfoResult = {
  Success: boolean;
};

export const RegisterUserWebPushInfo = async (
  param: RegisterUserWebPushInfoParam
): Promise<RegisterUserWebPushInfoResult> => {
  const req = `mutation { RegisterUserWebPushInfo(input: { EndPoint: "${param.endPoint}", PublicKey: "${param.publicKey}", AuthInfo: "${param.authInfo}" } ) { Success } }`;
  const ret = await execute(req);
  return ret.RegisterUserWebPushInfo as RegisterUserWebPushInfoResult;
};

type UnregisterUserWebPushInfoResult = {
  Success: boolean;
};

export const UnregisterUserWebPushInfo = async (): Promise<UnregisterUserWebPushInfoResult> => {
  const req = `mutation { UnregisterUserWebPushInfo { Success } }`;
  const ret = await execute(req);
  return ret.UnregisterUserWebPushInfo as UnregisterUserWebPushInfoResult;
};

export type UpdateAccountSymbolAvailableResult = {
  Success: boolean;
};
type UpdateAccountSymbolAvailableParam = {
  account: string;
  isin: string;
  available: boolean;
};
export const UpdateAccountSymbolAvailable = async (
  param: UpdateAccountSymbolAvailableParam
): Promise<UpdateAccountSymbolAvailableResult> => {
  const req = `mutation { UpdateAccountSymbolAvailable(input: { Account: "${param.account}", ISIN: "${param.isin}", Available: ${param.available} }) { Success } }`;
  const ret = await execute(req);

  return ret.UpdateAccountSymbolAvailable as UpdateAccountSymbolAvailableResult;
};
